/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-02-10 13:39:49
 */
import { useCallback } from 'react'
import { WatermarkItem } from '../context/WatermarkBatchContext'
import {
  fetchGroupMarksContent,
  fetchWatermarkItemsList
} from '../../../../actions/watermark'
import { useAppDispatch, useUserId, useGroupID } from '../../../../app/hooks'

/**
 * 用于获取水印内容的自定义Hook
 * @returns 获取水印内容的函数
 */
export const useWatermarkContent = () => {
  const dispatch = useAppDispatch()
  const userID = useUserId()
  const groupID = useGroupID()

  /**
   * 获取水印内容
   * @param watermarkID 水印ID
   * @param watermarkData 水印数据数组
   * @returns 更新后的水印数据数组
   */
  const fetchWatermarkContent = useCallback(
    async (watermarkID: string, watermarkData: WatermarkItem[]) => {
      if (!watermarkID || watermarkID === '') {
        return watermarkData
      }

      try {
        // 先请求获取 contentEdit
        const contentResponse = await dispatch(
          fetchGroupMarksContent({
            IDInfoList: [watermarkID]
          })
        )

        // 处理水印内容响应
        const contentList = contentResponse?.contentList || []
        if (contentList.length === 0) {
          return watermarkData
        }

        const filterWatermarkContent = contentList.find(
          (item) => item.watermarkID === watermarkID
        )
        if (!filterWatermarkContent) {
          return watermarkData
        }

        const watermarkContent =
          JSON.parse(filterWatermarkContent.content || '{}') || ''

        // 检查 contentEdit 中是否需要选项数据
        // 需要请求 fetchWatermarkItemsList 的情况：
        // 1. switchStatus === true 且 editType === 3 (级联选择) 且 isMultiLevelInput === true
        //    这种情况下，员工可以手动输入，需要获取选项数据作为预设选项
        // 2. switchStatus === true 且 editType === 2 (预设选项) 且 isMultiChoice === true
        //    这种情况下，员工可以多选，需要获取选项数据
        const needsOptions = watermarkContent.items?.some(
          (item: any) =>
            (item.switchStatus &&
              item.contentEdit?.editType === 3 &&
              item.contentEdit?.isMultiLevelInput) ||
            (item.switchStatus &&
              item.contentEdit?.editType === 2 &&
              item.contentEdit?.isMultiChoice === true)
        )

        // 如果需要选项数据，请求 fetchWatermarkItemsList
        let itemsResponse: any = null
        if (needsOptions) {
          itemsResponse = await dispatch(
            fetchWatermarkItemsList({
              userID: userID,
              watermarkID: watermarkID,
              groupID: groupID
            })
          )
        }

        const updatedWatermarkData = [...watermarkData].map((watermarkItem) => {
          const id = watermarkItem.item_id
          // 匹配时同时支持 string 和 number 类型的 ID
          const contentItem = watermarkContent.items?.find(
            (item: any) =>
              String(item.id) === String(id) ||
              String(item.item_id) === String(id)
          )

          // 优先使用 contentItem 中的 edit_type，如果没有则使用 watermarkItem 中的
          const editType = contentItem?.edit_type ?? watermarkItem.edit_type

          if (contentItem && contentItem.contentEdit) {
            // 如果已经请求了选项数据，尝试从响应中查找当前 item 的选项
            let options: {
              itemId: number
              records: {
                content: string
                childs: any[]
                uploadLocation: any[]
              }[]
            }[] = []

            if (itemsResponse) {
              // 从multipleItems中查找
              const multipleItem = itemsResponse?.multipleItems?.find(
                (item: any) => item.itemId === parseInt(id)
              )

              // 从items中查找
              const singleItem = itemsResponse?.items?.find(
                (item: any) => item.itemId === parseInt(id)
              )

              // 如果找到数据，添加到options中
              if (multipleItem) {
                options.push(multipleItem)
              }
              if (singleItem) {
                options.push(singleItem)
              }
            }

            return {
              ...watermarkItem,
              // 使用 contentItem 中的 edit_type（如果存在），否则使用 watermarkItem 中的
              edit_type: editType,
              // 使用新的 contentEdit，但如果原有 watermarkItem 有 contentEdit 且新的没有，保留原有的
              contentEdit: contentItem.contentEdit || watermarkItem.contentEdit,
              // 如果有新的 options，使用新的；否则保留原有的
              ...(options.length > 0
                ? { options: options }
                : watermarkItem.options && { options: watermarkItem.options })
            }
          }

          // 即使没有找到 contentItem 或 contentItem.contentEdit，也要保留原有的 contentEdit 和其他信息
          // 使用展开运算符确保保留所有原有字段（包括 contentEdit、options 等）
          return {
            ...watermarkItem,
            edit_type: editType
            // 所有其他字段（包括 contentEdit、options 等）都会通过 ...watermarkItem 保留
          }
        })

        return updatedWatermarkData
      } catch (error) {
        console.error('获取水印内容失败:', error)
        return watermarkData
      }
    },
    [dispatch, userID, groupID]
  )

  return { fetchWatermarkContent }
}
